import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Card,
    CardHeader,
    CardContent,
    Avatar,
    TextField,
    Typography,
} from '@material-ui/core';
import AutoComplete from '@material-ui/lab/Autocomplete';
import { UPDATE_SETUP } from '../../../store/setup';
import { WarningIconWithTooltip } from '../../../../../../ZaddonsJSLibs/common-client2/src/components';
import { ConfigurationSetWarningCodes } from '../../../../Enums';

const InputTitle = (props) => (
    <Typography
        component="p"
        className="input-label"
        {...props}
    >
        {props.children}
    </Typography>
);

const AutoCompleteWithWarning = ({ warningType, warnings, children }) => {
    const { t } = useTranslation('shiftBidding', { keyPrefix: 'admin.warnings' });
    const hasWarning = warnings && warnings.includes(warningType);
    
    if (!hasWarning) {
        return children;
    }
    
    const warningMessage = t(warningType);
    
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ flex: 1 }}>
                {children}
            </div>
            <WarningIconWithTooltip
                tooltipWarnings={[warningMessage]}
                fontSize="medium"
            />
        </div>
    );
};

const InputDisclaimer = (props) => (
    <p {...props}>
        <small className="input-disclaimer">
            {props.children}
        </small>
    </p>
);

/**
 * @param {{
 *  stepIndex: number,
 *  validationErrors: {[Key: string] : boolean},
 *  disabledSections: {[Key: string] : boolean},
 *  errorRef: import('react').MutableRefObject<HTMLInputElement>,
 * }} props
 */
function AccessSection({
    stepIndex,
    validationErrors,
    disabledSections,
    errorRef,
}) {
    const dispatch = useDispatch();
    const { t } = useTranslation('shiftBidding', { keyPrefix: 'admin.setupEdit.accessStep' });
    /** @type {ShiftBidding.AdminStore} */
    // @ts-ignore
    const { adminSetup, adminAppData } = useSelector((state) => state);
    const {
        creationHyperfindId,
        managementHyperfindId,
        viewHyperfindId,
        warnings,
    } = adminSetup;
    const { hyperfinds } = adminAppData;

    /**
     * @param {string} target
     * @param {number} value
     */
    const updateSetupStore = (target, value) => {
        dispatch({
            type: UPDATE_SETUP,
            payload: {
                [target]: value,
            },
        });
    };

    return (
        <Card className="card">
            <CardHeader
                ref={errorRef}
                className="card--header"
                color="textSecondary"
                gutterBottom
                avatar={<Avatar className="avatar">{stepIndex}</Avatar>}
                title={t('title')}
            />
            <CardContent>
                <div>
                    <InputTitle>{t('creationManagerInput.label')}</InputTitle>
                    <AutoComplete
                        id="create-hyperfind-select"
                        value={
                            hyperfinds.find((x) => x.value === creationHyperfindId)

                        }
                        options={hyperfinds}
                        onChange={(_event, optionSelected) => updateSetupStore('creationHyperfindId', optionSelected.value)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                error={validationErrors.creationHyperfindId}
                                variant="outlined"
                                placeholder={t('creationManagerInput.placeholder')}
                            />
                        )}
                        getOptionLabel={(option) => option.label}
                        includeInputInList
                        disabled={disabledSections.creationHyperfindId}
                        disableClearable
                    />
                    {
                        validationErrors.creationHyperfindId ? (
                            <span style={{ color: 'red' }}>{t('creationManagerInput.error')}</span>) : null
                    }
                    <InputDisclaimer>
                        {t('creationManagerInput.disclaimer')}
                    </InputDisclaimer>
                </div>
                <div>
                    <InputTitle>{t('managementManagerInput.label')}</InputTitle>
                    <AutoCompleteWithWarning 
                        warningType={ConfigurationSetWarningCodes.IsFailingManagementHyperfindExecution}
                        warnings={warnings}
                    >
                        <AutoComplete
                            id="management-hyperfind-select"
                            value={
                                hyperfinds.find((x) => x.value === managementHyperfindId)
                            }
                            options={hyperfinds}
                            onChange={(_event, optionSelected) => updateSetupStore('managementHyperfindId', optionSelected.value)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    error={validationErrors.managementHyperfindId}
                                    variant="outlined"
                                    placeholder={t('managementManagerInput.placeholder')}
                                />
                            )}
                            getOptionLabel={(option) => option.label}
                            includeInputInList
                            disabled={disabledSections.managementHyperfindId}
                            disableClearable
                        />
                    </AutoCompleteWithWarning>
                    {
                        validationErrors.managementHyperfindId ? (
                            <span style={{ color: 'red' }}>{t('managementManagerInput.error')}</span>) : null
                    }
                    <InputDisclaimer>
                        {t('managementManagerInput.disclaimer')}
                    </InputDisclaimer>
                </div>
                <div>
                    <InputTitle>{t('viewManagerInput.label')}</InputTitle>
                    <AutoCompleteWithWarning 
                        warningType={ConfigurationSetWarningCodes.IsFailingViewHyperfindExecution}
                        warnings={warnings}
                    >
                        <AutoComplete
                            id="view-hyperfind-select"
                            value={
                                hyperfinds.find((x) => x.value === viewHyperfindId)
                            }
                            options={hyperfinds}
                            onChange={(_event, optionSelected) => updateSetupStore('viewHyperfindId', optionSelected.value)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    error={validationErrors.viewHyperfindId}
                                    variant="outlined"
                                    placeholder={t('viewManagerInput.placeholder')}
                                />
                            )}
                            getOptionLabel={(option) => option.label}
                            includeInputInList
                            disabled={disabledSections.viewHyperfindId}
                            disableClearable
                        />
                    </AutoCompleteWithWarning>
                    {
                        validationErrors.viewHyperfindId ? (
                            <span style={{ color: 'red' }}>{t('viewManagerInput.error')}</span>) : null
                    }
                    <InputDisclaimer>
                        {t('viewManagerInput.disclaimer')}
                    </InputDisclaimer>
                </div>
            </CardContent>
        </Card>

    );
}

export default AccessSection;
